import * as React from 'react';
import { TPaymentMethod } from './PaymentMethod.types';
import './PaymentMethod.styled.scss';
import {
  Button,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
} from '@mui/material';
import { TPaymentMethod as TPaymentMethodType } from '../../Checkout.types';
import { PAYMENT_METHODS } from '../../Checkout.constants';
import {
  CreditCard,
  AccountBalance,
  Payment,
  LocalAtm,
} from '@mui/icons-material';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import { useMobileStickyUntilVisible } from 'hooks/useMobileStickyUntilVisible';

const paymentIcons = {
  card: CreditCard,
  blik: Payment,
  transfer: AccountBalance,
  cash: LocalAtm,
};

export const PaymentMethod: TPaymentMethod = ({
  selectedMethod,
  onMethodSelect,
  onComplete,
  onBack,
  subtotal,
  shippingCost,
}) => {
  const localize = useLocalize();
  const total = subtotal + shippingCost;
  const actionsRef = React.useRef<HTMLDivElement | null>(null);
  const shouldShowStickyActions = useMobileStickyUntilVisible({
    targetRef: actionsRef,
    dependencies: [selectedMethod],
  });

  const handleSubmit = () => {
    if (selectedMethod) {
      onComplete(selectedMethod);
    }
  };

  return (
    <div className="payment-method">
      <div className="payment-method__header">
        <h6 className="payment-method__title">
          {localize('checkout.payment.title')}
        </h6>
      </div>

      <div className="payment-method__content">
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedMethod || ''}
            onChange={(e) =>
              onMethodSelect(e.target.value as TPaymentMethodType)
            }
          >
            {PAYMENT_METHODS.map((method) => {
              const IconComponent = paymentIcons[method.id];
              return (
                <Card
                  key={method.id}
                  className={`payment-method__card ${
                    selectedMethod === method.id
                      ? 'payment-method__card--selected'
                      : ''
                  }`}
                >
                  <CardContent>
                    <FormControlLabel
                      value={method.id}
                      control={<Radio />}
                      label={
                        <div className="payment-method__info">
                          <div className="payment-method__header-row">
                            {IconComponent && (
                              <IconComponent className="payment-method__icon" />
                            )}
                            <div className="payment-method__text">
                              <h6>
                                {localize(`checkout.payment.${method.id}.name`)}
                              </h6>
                              <p className="body2">
                                {localize(
                                  `checkout.payment.${method.id}.description`
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      }
                    />
                  </CardContent>
                </Card>
              );
            })}
          </RadioGroup>
        </FormControl>
      </div>

      <div className="payment-method__summary">
        <div className="payment-method__summary-row">
          <p>{localize('checkout.payment.summary.subtotal')}</p>
          <p>
            {localize('checkout.shipping.price.zloty', [subtotal.toFixed(2)])}
          </p>
        </div>
        <div className="payment-method__summary-row">
          <p>{localize('checkout.payment.summary.shipping')}</p>
          <p>
            {shippingCost === 0
              ? localize('checkout.payment.summary.shipping.free')
              : localize('checkout.shipping.price.zloty', [
                  shippingCost.toFixed(2),
                ])}
          </p>
        </div>
        <Divider className="payment-method__summary-divider" />
        <div className="payment-method__summary-row payment-method__summary-row--total">
          <h6>{localize('checkout.payment.summary.total')}</h6>
          <h6>
            {localize('checkout.shipping.price.zloty', [total.toFixed(2)])}
          </h6>
        </div>
      </div>

      <div className="payment-method__actions" ref={actionsRef}>
        <Button
          variant="outlined"
          onClick={onBack}
          className="payment-method__back-button"
        >
          {localize('checkout.payment.button.back')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedMethod}
        >
          {localize('checkout.payment.button.complete')}
        </Button>
      </div>
      <div
        className={`payment-method__mobile-sticky-actions${
          shouldShowStickyActions
            ? ' payment-method__mobile-sticky-actions--visible'
            : ''
        }`}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          className="payment-method__back-button"
        >
          {localize('checkout.payment.button.back')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedMethod}
        >
          {localize('checkout.payment.button.complete')}
        </Button>
      </div>
    </div>
  );
};
